import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {BRAND_SWATCHES, normalizeHexColor} from './config/brandSwatches';

type Props = {
	value: string;
	onChange: (color: string) => void;
};

const Row = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
`;

const Swatch = styled.button<{$color: string; $selected: boolean}>`
	width: 28px;
	height: 28px;
	border-radius: 4px;
	border: 2px solid ${p => (p.$selected ? '#000' : 'transparent')};
	background: ${p => p.$color};
	cursor: pointer;
	padding: 0;
`;

// Free hex entry beside the swatches — the design system has no color input primitive.
const HexInput = styled.input<{$invalid: boolean}>`
	width: 92px;
	height: 30px;
	padding: 0 8px;
	font-size: 13px;
	border: 1px solid ${p => (p.$invalid ? '#c00' : '#d3d3df')};
	border-radius: 4px;
	background: #fff;
`;

const BrandColorSwatchPicker: React.FC<Props> = ({value, onChange}) => {
	const [hexDraft, setHexDraft] = useState(value.toUpperCase());
	useEffect(() => {
		setHexDraft(value.toUpperCase());
	}, [value]);
	const invalid = normalizeHexColor(hexDraft) == null;

	return (
		<Row role="group" aria-label="Brand color">
			{BRAND_SWATCHES.map(color => (
				<Swatch
					key={color}
					type="button"
					$color={color}
					$selected={color.toLowerCase() === value.toLowerCase()}
					aria-pressed={color.toLowerCase() === value.toLowerCase()}
					aria-label={`Select brand color ${color}`}
					onClick={() => onChange(color)}
				/>
			))}
			<HexInput
				value={hexDraft}
				$invalid={invalid}
				spellCheck={false}
				aria-label="Brand color hex"
				data-cy="quote-brand-color-hex"
				onChange={event => {
					const next = event.target.value;
					setHexDraft(next.toUpperCase());
					const normalized = normalizeHexColor(next);
					if (normalized) {
						onChange(normalized);
					}
				}}
			/>
		</Row>
	);
};

export default BrandColorSwatchPicker;
